import {
  StoreUsersListQuery,
  useRemoveStoreUserMutation,
  useStoreUserRemovalPreviewQuery,
} from '@/dealers/_gen/gql'
import Ghost from '@/gf/components/Ghost'
import ModalForm from '@/gf/components/ModalForm'
import SelectField from '@/gf/components/SelectField'
import YellowAlert from '@/gf/components/YellowAlert'
import useMsgs from '@/gf/hooks/useMsgs'
import { ModalSize } from '@/types'
import { useState } from 'react'

type User = NonNullable<StoreUsersListQuery['session']['store']>['users'][number]

const UNASSIGNED = ''

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

const RemoveUserModal = ({
  user,
  users,
  onClose,
  onSuccess,
}: {
  user: User
  users: User[]
  onClose: () => void
  onSuccess: () => void
}) => {
  const [_, msgs] = useMsgs()
  const [moveTo, setMoveTo] = useState(UNASSIGNED)
  const { data } = useStoreUserRemovalPreviewQuery({
    variables: { userId: user.id },
    fetchPolicy: 'network-only',
  })
  const [removeUser, { loading: removing }] = useRemoveStoreUserMutation()

  const preview = data?.storeUserRemovalPreview
  const name = user.name || user.email
  const recipients = users.filter((u) => u.id !== user.id)

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      await removeUser({
        variables: { userId: user.id, moveRequestsToUserId: moveTo || null },
      })

      msgs.add(`${name} was removed from your account`, 'positive')
      onClose()
      onSuccess()
    } catch (error) {
      const err = error as { graphQLErrors?: { message: string }[] }
      const message = (err.graphQLErrors ?? []).map((e) => e.message).join(' ')
      msgs.add(message || 'Could not remove the user. Try again.', 'negative')
    }
  }

  return (
    <ModalForm
      open
      onClose={onClose}
      title={`Remove ${name}?`}
      size={ModalSize.SM}
      onSubmit={onSubmit}
      submitButtonText="Remove User"
      submitButtonDisabled={!preview}
      submitButtonShowSpinner={removing}
    >
      <div className="space-y-4 text-sm text-gray-700">
        <p>
          {name} will no longer be able to open your account. Their Gearflow login stays active, and
          their request history stays as it is.
        </p>

        {!preview ? (
          <Ghost className="block h-16 w-full" />
        ) : (
          <>
            {preview.openRequestCount > 0 ? (
              <div className="space-y-2">
                <p>
                  {name} owns{' '}
                  <span className="font-medium">
                    {plural(preview.openRequestCount, 'open request', 'open requests')}
                  </span>
                  .
                </p>

                <SelectField
                  label="Move their open requests to"
                  currentId={moveTo}
                  onChange={setMoveTo}
                  options={[
                    { id: UNASSIGNED, display: 'Nobody (leave them Unassigned)' },
                    ...recipients.map((u) => ({ id: u.id, display: u.name || u.email })),
                  ]}
                />

                {moveTo === UNASSIGNED && (
                  <p className="text-gray-500">
                    You can find them later with the Unassigned filter on the Requests page.
                  </p>
                )}
              </div>
            ) : (
              <p>{name} owns no open requests.</p>
            )}

            {preview.soleContactCustomerCount > 0 && (
              <YellowAlert
                title={`${name} is the only contact for ${plural(
                  preview.soleContactCustomerCount,
                  'customer',
                  'customers'
                )}`}
              >
                After you remove them, new requests from{' '}
                {preview.soleContactCustomerCount === 1 ? 'that customer' : 'those customers'} will
                not reach anyone at your store. Ask the customer to add another contact for you
                first.
              </YellowAlert>
            )}
          </>
        )}
      </div>
    </ModalForm>
  )
}

export default RemoveUserModal
